import produce from 'immer';
import logAlignmentReport, { isAlignmentDebugOn } from './alignmentReport';
import enrichClassifications from './enrichClassifications';
import * as matchingRules from './matchingRules';
import * as similarityRules from './similarityRules';
import * as sortingRules from './sortingRules';
import getUniqueCategoriesAndDescriptors from './unique';
export const lexicalMapCompare = (a, b) => String(a[0].localeCompare(b[0]));
const prepareComparisonDocs = (anchorDocId, comparisonDocuments, useSimilarity = true) => {
    const documents = [];
    const anchorTags = new Map();
    const anchorTagsByKey = new Map();
    const extraTags = new Map();
    const clauseOutcomes = [];
    const debugOn = isAlignmentDebugOn();
    if (!anchorDocId || comparisonDocuments.length === 0) {
        return {
            tagsColumn: new Map(),
            documents,
            categories: [],
            descriptors: [],
        };
    }
    const { categories, descriptors } = getUniqueCategoriesAndDescriptors(comparisonDocuments);
    const enrichedDocuments = enrichClassifications(comparisonDocuments);
    const anchorDoc = enrichedDocuments.find(doc => doc.documentComparisonFileId === anchorDocId);
    if (!anchorDoc) {
        throw Error(`Failed To Find Anchor Doc: ${anchorDocId}`);
    }
    anchorDoc.classifications.forEach(classification => {
        anchorTags.set(classification.name, classification);
        anchorTagsByKey.set(matchingRules.normalizeTag(classification.name), classification);
    });
    enrichedDocuments.forEach(doc => {
        let classificationMap = new Map();
        const classifications = Array.from(doc.classifications);
        const tagMatchedRowKeys = new Set();
        const tagMatches = classifications.map(classification => {
            var _a;
            const tagMatch = getClassificationMapKeysByRules(anchorTagsByKey, classification, false);
            (_a = tagMatch === null || tagMatch === void 0 ? void 0 : tagMatch.keys) === null || _a === void 0 ? void 0 : _a.forEach(key => tagMatchedRowKeys.add(key.rowKey));
            return tagMatch;
        });
        classifications.forEach((classification, index) => {
            var _a, _b, _c, _d, _e;
            const match = tagMatches[index]
                || getClassificationMapKeysByRules(anchorTagsByKey, classification, useSimilarity, tagMatchedRowKeys);
            if (!((_a = match === null || match === void 0 ? void 0 : match.keys) === null || _a === void 0 ? void 0 : _a.length)) {
                extraTags.set(classification.name, classification);
            }
            const classificationMapKeys = (match === null || match === void 0 ? void 0 : match.keys) || [{
                    rowKey: classification.name,
                    tabKey: classification.name,
                }];
            const mappedClassification = (match === null || match === void 0 ? void 0 : match.similarityScore) !== undefined
                ? Object.assign(Object.assign({}, classification), { similarityMatch: { score: match.similarityScore } }) : classification;
            classificationMap = addClassificationToMap(classificationMap, classificationMapKeys, mappedClassification);
            const isComparator = doc.documentComparisonFileId !== anchorDocId;
            const nearMiss = debugOn && useSimilarity && !match && isComparator
                && ((_b = classification.embedding) === null || _b === void 0 ? void 0 : _b.length)
                ? similarityRules.getBestSimilarityCandidate(anchorTagsByKey, classification)
                : undefined;
            clauseOutcomes.push({
                documentComparisonFileId: doc.documentComparisonFileId,
                fileName: doc.fileName,
                isAnchor: !isComparator,
                name: classification.name,
                category: classification.category,
                hasEmbedding: !!((_c = classification.embedding) === null || _c === void 0 ? void 0 : _c.length),
                rule: match === null || match === void 0 ? void 0 : match.rule,
                matchedRowKey: (_e = (_d = match === null || match === void 0 ? void 0 : match.keys) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.rowKey,
                similarityScore: match === null || match === void 0 ? void 0 : match.similarityScore,
                nearMiss: nearMiss || undefined,
            });
        });
        const newDoc = produce(doc, draft => {
            draft.classificationMap = classificationMap;
            delete draft.classifications;
        });
        documents.push(newDoc);
    });
    const tagsColumn = new Map();
    const orderedClassifierKeys = sortTagsByRules(anchorTags, extraTags);
    for (const classifier of orderedClassifierKeys) {
        const tag = anchorTags.get(classifier) || extraTags.get(classifier);
        tagsColumn.set(classifier, tag);
    }
    logAlignmentReport(clauseOutcomes, useSimilarity);
    return { tagsColumn, documents, categories, descriptors };
};
const getClassificationMapKeysByRules = (anchorTags, classification, useSimilarity, blockedSimilarityRowKeys) => {
    const exactMatchKey = matchingRules.getExactMatchKey(anchorTags, classification);
    if (exactMatchKey)
        return { keys: [exactMatchKey], rule: 1 };
    const matchingParentKey = matchingRules.getMatchingParentKey(anchorTags, classification);
    if (matchingParentKey)
        return { keys: [matchingParentKey], rule: 2 };
    const matchingChildKeys = matchingRules.getMatchingChildKeys(anchorTags, classification);
    if (matchingChildKeys === null || matchingChildKeys === void 0 ? void 0 : matchingChildKeys.length)
        return { keys: matchingChildKeys, rule: 3 };
    const exactMatchDescriptor = matchingRules.getExactMatchDescriptorInCategoryFamily(anchorTags, classification);
    if (exactMatchDescriptor === null || exactMatchDescriptor === void 0 ? void 0 : exactMatchDescriptor.length)
        return { keys: exactMatchDescriptor, rule: 4 };
    const matchingDescriptorParent = matchingRules.getDescriptorParentInCategoryFamily(anchorTags, classification);
    if (matchingDescriptorParent === null || matchingDescriptorParent === void 0 ? void 0 : matchingDescriptorParent.length)
        return { keys: matchingDescriptorParent, rule: 5 };
    const matchingDescriptorChildren = matchingRules.getDescriptorChildrenInCategoryFamily(anchorTags, classification);
    if (matchingDescriptorChildren === null || matchingDescriptorChildren === void 0 ? void 0 : matchingDescriptorChildren.length)
        return { keys: matchingDescriptorChildren, rule: 6 };
    if (useSimilarity) {
        const similarityMatch = similarityRules.getSimilarityMatchKey(anchorTags, classification);
        if (similarityMatch && !(blockedSimilarityRowKeys === null || blockedSimilarityRowKeys === void 0 ? void 0 : blockedSimilarityRowKeys.has(similarityMatch.key.rowKey))) {
            return { keys: [similarityMatch.key], rule: 7, similarityScore: similarityMatch.score };
        }
    }
    return undefined;
};
const addClassificationToMap = (classificationMap, classificationMapKeys, classification) => {
    for (const classificationMapKey of classificationMapKeys) {
        const existingRow = classificationMap.get(classificationMapKey.rowKey);
        if (existingRow) {
            const existingTabIndex = existingRow.findIndex(tab => tab.name === classificationMapKey.tabKey);
            if (existingTabIndex !== -1) {
                existingRow[existingTabIndex].classifications.push(classification);
            }
            else {
                existingRow.push({
                    name: classificationMapKey.tabKey,
                    classifications: [classification],
                });
            }
        }
        else {
            classificationMap.set(classificationMapKey.rowKey, [{
                    name: classificationMapKey.tabKey,
                    classifications: [classification],
                }]);
        }
    }
    return classificationMap;
};
const sortTagsByRules = (anchorTags, extraTags) => {
    let orderedClassifierKeys = [];
    orderedClassifierKeys = sortingRules.sortAnchorTagsByAnchorDocOrder(orderedClassifierKeys, anchorTags);
    orderedClassifierKeys = sortingRules.sortExtraTagsGroupedByCategory(orderedClassifierKeys, extraTags);
    return orderedClassifierKeys;
};
export default prepareComparisonDocs;
